import { skipToken } from "@reduxjs/toolkit/query/react";
import { QueryStatus } from "@sscs/types";
import { getQueryStatus } from "@sscs/utils";
import { useGetVersionsQuery } from "Services";
import { Version } from "Types";

type VersionsSummary = {
  versions: Version[];
  queryStatus: QueryStatus;
  selectedPredecessorVersion: string | undefined;
  pushedOutVersion: string | undefined;
  maxVersionsLimit: number;
};

const useVersions = (
  selectedPackageId?: string,
  selectedPredecessorId?: string | null,
): VersionsSummary => {
  const { data, isLoading, isFetching, isError, error } = useGetVersionsQuery(
    selectedPackageId ? { packageId: selectedPackageId } : skipToken,
  );

  if (!selectedPackageId) {
    const queryStatus: QueryStatus = { status: "SUCCESS" };
    return {
      versions: [],
      queryStatus,
      pushedOutVersion: undefined,
      selectedPredecessorVersion: undefined,
      maxVersionsLimit: 0,
    };
  }

  const queryStatus = getQueryStatus({
    data,
    isLoading,
    isFetching,
    isError,
    error,
  });

  if (queryStatus.status === "FETCHING") {
    queryStatus.status = "LOADING"; // actual desired behaviour here and workaround for stale form in QueryResult
  }

  const maxVersionsLimit = data?.max_versions_per_package ?? 0;
  const selectedPredecessorVersion = data?.versions.find(
    (version) => version.version_id === selectedPredecessorId,
  )?.software_info.version;

  return {
    queryStatus,
    selectedPredecessorVersion,
    maxVersionsLimit,
    versions: data?.versions ?? [],
    pushedOutVersion:
      data?.versions?.[maxVersionsLimit - 1]?.software_info.version,
  };
};

export default useVersions;
